import React, { useState, useEffect, useRef } from "react";
import {
  Group,
  FileButton,
  Button,
  Text,
  Grid,
  Divider,
  Anchor,
  Loader,
  Switch,
  Stack,
  Box,
  Paper,
  Title,
  MultiSelect,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useServerApi } from "../hooks/userServerApi";
import {
  IconTrash,
  IconCheck,
  IconSettings,
  IconExclamationMark,
  IconMessageCircle,
  IconCoin,
  IconDownload,
  IconDotsVertical,
  IconCircleCheck,
  IconCircleDashed,
  IconUpload,
} from "@tabler/icons";

import { List, ThemeIcon } from "@mantine/core";
import RemoteSelect2 from "./remoteSelect2";

const CommissionScheduleFileImporter = ({ form, name }) => {
  const [file, setFile] = useState(null);
  const id = form.values._id;
  const [fileData, setFileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [nonGiOnly, setNonGiOnly] = useState(false);
  const [api] = useServerApi();

  // Track whether initial saved state has been loaded
  const [initialized, setInitialized] = useState(false);
  const [productsFetchEnabled, setProductsFetchEnabled] = useState(false);
  const userEditedFilters = useRef(false);

  // Load saved filter state when component mounts
  useEffect(() => {
    if (form.values._id) {
      if (form.values.downloadFilterProvider) {
        const providerId =
          typeof form.values.downloadFilterProvider === "object"
            ? form.values.downloadFilterProvider._id
            : form.values.downloadFilterProvider;
        setSelectedProvider(providerId);
      }
      if (
        form.values.downloadFilterProducts &&
        Array.isArray(form.values.downloadFilterProducts)
      ) {
        const productIds = form.values.downloadFilterProducts.map((p) =>
          typeof p === "object" ? p._id : p,
        );
        setSelectedProducts(productIds);
      }
      if (form.values.downloadFilterNonGI !== undefined) {
        setNonGiOnly(form.values.downloadFilterNonGI);
      }
      setInitialized(true);
    }
  }, [form.values._id]);

  // Fetch product options when user opens product filter or changes provider
  useEffect(() => {
    if (!initialized || !productsFetchEnabled) return;

    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const query = { status: "ACTIVE" };
        if (selectedProvider) {
          query.provider = selectedProvider;
        }
        const result = await api.search({
          apiEntity: "product",
          pageSize: 500,
          currentPage: 1,
          searchQuery: query,
          sort: { by: "name", order: "asc" },
        });
        const options = result.docs.map((p) => ({
          value: p._id,
          label: `[${p.code ?? ""}] ${p.name ?? ""}`,
        }));
        setProductOptions(options);

        // If we have saved product IDs, make sure they're all in the options
        if (selectedProducts && selectedProducts.length > 0) {
          const currentOptionIds = options.map((o) => o.value);
          const selectedProductIds = selectedProducts.map((p) =>
            typeof p === "object" ? p._id : p,
          );
          const missingProductIds = selectedProductIds.filter(
            (pid) => !currentOptionIds.includes(pid),
          );

          if (missingProductIds.length > 0) {
            const missingResult = await api.search({
              apiEntity: "product",
              pageSize: 500,
              currentPage: 1,
              searchQuery: { _id: { $in: missingProductIds } },
              sort: { by: "name", order: "asc" },
            });
            const additionalOptions = missingResult.docs.map((p) => ({
              value: p._id,
              label: `[${p.code ?? ""}] ${p.name ?? ""}`,
            }));
            setProductOptions([...options, ...additionalOptions]);
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, [initialized, productsFetchEnabled, selectedProvider]);

  // Save filter state to form and persist to database
  const saveFilterState = async () => {
    if (!id) return;

    try {
      // Update form state
      form.setFieldValue("downloadFilterProvider", selectedProvider);
      form.setFieldValue("downloadFilterProducts", selectedProducts);
      form.setFieldValue("downloadFilterNonGI", nonGiOnly);

      // Persist to database
      await api.CommissionSchedule.updateFilterState(id, {
        downloadFilterProvider: selectedProvider,
        downloadFilterProducts: selectedProducts,
        downloadFilterNonGI: nonGiOnly,
      });
    } catch (error) {
      console.log("Error saving filter state:", error);
    }
  };

  // Debounced auto-save when user changes filters (not on initial hydration)
  useEffect(() => {
    if (!id || !initialized || !userEditedFilters.current) return;

    const timeoutId = setTimeout(() => {
      saveFilterState();
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [selectedProvider, selectedProducts, nonGiOnly, initialized]);

  const handleUploadSuccess = (file, data) => {
    showNotification({
      title: `${file.name} Import successfully`,
      message: "Import Success",
    });
    //For UI display
    setFile(file);
    setFileData(data);

    //Set file content
    const { content } = data;
    console.log(content);
    form.setFieldValue("rows", content.rows);
    form.setFieldValue("total", content.total);
    form.setFieldValue("totalCommission", content.totalCommission);
  };

  const handleUploadFail = (file, error) => {
    showNotification({
      title: `${file.name} Import fail`,
      color: "red",
      icon: <IconExclamationMark size={18} />,
      message: error?.msg,
    });
    setFile(null);
    setFileData(null);
    form.setFieldValue(name, null);
    setError(error.msg);
  };

  const handleFileUpload = async (file) => {
    console.log("handleFileUpload", file, id);
    try {
      if (!file || !id) return;
      setLoading(true);
      setError(null);
      setFile(file);
      let result = await api.CommissionSchedule.import(file, id);
      console.log(result);

      if (result.success) {
        form.setFieldValue("productList", result.data.productList);
        form.setFieldValue("totalRows", result.data.totalRows);
        form.setFieldValue("fileImportDate", result.data.fileImportDate);
        form.setFieldValue("fileImportBy", result.data.fileImportBy);
        form.setFieldValue("fileName", result.data.fileName);
        form.refreshProductList?.();
        showNotification({
          title: `${file.name} imported successfully`,
          message: "Product list updated",
          color: "teal",
          icon: <IconCircleCheck size={18} />,
        });
      } else {
        setError(result.error?.msg || "Unknown error occurred");
      }

      // let { data, success, error } = result;
      // success ? handleUploadSuccess(file, data) : handleUploadFail(file, error);
      setLoading(false);
    } catch (error) {
      setError(error.message || "An error occurred");
      setLoading(false);
    }
  };
  return (
    <>
      {/* Download Template Section */}
      <Paper withBorder p="md" mb="xl">
        <Title order={6} mb="md">
          Download Template
        </Title>
        <Stack spacing="md" style={{ maxWidth: 400 }}>
          <RemoteSelect2
            apiEntity="provider"
            select="name cname code shortName _id"
            pageSize={200}
            label="Filter by Provider (Empty for all)"
            placeholder="Select provider..."
            size="xs"
            searchFields={["name", "code", "cname"]}
            valueField="_id"
            labelField="name"
            labelRender={(data) => {
              return `[${data.code ?? ""}] ${data.name ?? ""} ${data.cname ?? ""}`;
            }}
            value={selectedProvider}
            onDataChange={(data) => {
              userEditedFilters.current = true;
              setSelectedProvider(data?._id || null);
              setSelectedProducts([]);
              setProductsFetchEnabled(true);
            }}
            clearable={true}
            sort={{ by: "name", order: "asc" }}
          />

          <MultiSelect
            label="Filter by Products (Empty for all)"
            placeholder={
              loadingProducts ? "Loading products..." : "Select products..."
            }
            size="xs"
            data={productOptions}
            value={selectedProducts}
            onChange={(values) => {
              userEditedFilters.current = true;
              setSelectedProducts(values);
            }}
            onDropdownOpen={() => setProductsFetchEnabled(true)}
            searchable
            clearable
            disabled={loadingProducts}
            maxDropdownHeight={200}
          />

          <Group position="apart">
            <Switch
              label="Non-GI Only"
              checked={nonGiOnly}
              onChange={(event) => {
                userEditedFilters.current = true;
                setNonGiOnly(event.currentTarget.checked);
              }}
              size="sm"
            />
            <Button
              size="xs"
              leftIcon={<IconDownload size="1rem" />}
              loading={downloadLoading}
              onClick={async () => {
                if (downloadLoading) return;
                setDownloadLoading(true);
                try {
                  await api.CommissionSchedule.downloadTemplate(
                    form.values.effectiveDate,
                    nonGiOnly ? "non-gi" : "all",
                    selectedProvider,
                    selectedProducts,
                  );
                } finally {
                  setDownloadLoading(false);
                }
              }}
            >
              Download
            </Button>
          </Group>
        </Stack>
      </Paper>

      {/* Upload Template Section */}
      <Paper withBorder p="md">
        <Title order={6} mb="md">
          Upload Template
        </Title>
        <Group style={{ width: 300 }}>
          <FileButton
            onChange={handleFileUpload}
            accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          >
            {(props) => (
              <Button
                {...props}
                size="xs"
                loading={loading}
                leftIcon={<IconUpload size="1rem" />}
              >
                Select File
              </Button>
            )}
          </FileButton>
        </Group>

        {!loading && (
          <List
            mt={"xl"}
            spacing="xs"
            size="sm"
            center
            icon={
              <ThemeIcon color="teal" size={24} radius="xl">
                <IconCircleCheck size={16} />
              </ThemeIcon>
            }
          >
            {file && (
              <>
                <List.Item>File format is valid. [OK]</List.Item>
                <List.Item>File {file.name} statement read. [OK]</List.Item>
                <List.Item>
                  Total {fileData?.content?.total} commission schedule rows
                  read. [OK]
                </List.Item>
              </>
            )}

            {error && (
              <List.Item
                icon={
                  <ThemeIcon color="red" size={24} radius="xl">
                    <IconExclamationMark size={16} />
                  </ThemeIcon>
                }
              >
                {error}
              </List.Item>
            )}
          </List>
        )}
      </Paper>
    </>
  );
};

export default CommissionScheduleFileImporter;
